import Logo from '@/components/logo/logo';
import { NavMenu } from '@/components/nav-menu/nav-menu';
import { Flex, Layout, Drawer } from 'antd';
import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './default.layout.module.scss';
import clsx from 'clsx';
import { useScreenSize } from '@/hooks/use-screen-size/use-screen-size';
import NavMobile from '@/components/nav-mobile/nav-mobile';
import { PageHeader } from '@/components/page-header/page-header';
import { UserChip } from '@/components/user-chip/user-chip';

interface DefaultLayoutProps {
  children: ReactNode;
}

// Nothing exposes collapse right now, so honouring a stored preference would strand the
// user in the icon rail. Nothing writes it either, so any existing
// `voicesphere.sidebarCollapsed` survives untouched — read that key here to restore the control.
const collapsed = false;

export default function DefaultLayout({ children }: DefaultLayoutProps) {
  const { t } = useTranslation('layout');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const screenSize = useScreenSize();

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const isSiderVisible = screenSize.width >= 1024 || isMenuOpen;
  const { Sider, Content } = Layout;

  return (
    <Layout data-testid="default-layout" hasSider className={styles.defaultLayout}>
      <NavMobile className={styles.navMobile} toggleMenu={toggleMenu} />

      {screenSize.width < 1024 && (
        <Drawer title={t('menu.title')} placement="left" onClose={toggleMenu} open={isMenuOpen} width={300}>
          <Flex vertical className={styles.defaultLayoutSiderContainer}>
            <Logo onLogoClick={toggleMenu} />
            <NavMenu onMenuItemClick={toggleMenu} />
            <UserChip />
          </Flex>
        </Drawer>
      )}

      {screenSize.width >= 1024 && (
        <Sider
          width="fit-content"
          collapsed={collapsed}
          trigger={null}
          className={clsx(
            styles.defaultLayoutSider,
            !isSiderVisible && styles.hidden,
            collapsed && styles.collapsed
          )}
        >
          <Flex
            data-testid="sider-container"
            vertical
            className={styles.defaultLayoutSiderContainer}
          >
            <div className={styles.logoButtonWrapper}>
              <Logo onLogoClick={toggleMenu} collapsed={collapsed} />
            </div>
            <NavMenu onMenuItemClick={toggleMenu} collapsed={collapsed} />
            <UserChip collapsed={collapsed} />
          </Flex>
        </Sider>
      )}

      <Layout
        data-testid="default-layout-content"
        className={clsx(styles.defaultLayoutContent, collapsed && styles.contentCollapsed)}
      >
        <Content>
          <PageHeader />
          {children}
        </Content>
      </Layout>
    </Layout>
  );
}
